import type { Point } from 'geojson';

import type { PlaceFeature, ReversePlaceFeature } from '@/modules/api/geocoder';
import { CustomSource, Source } from '@/modules/api/geocoder';
import { geocoderApiV1ReverseOptions } from '@/modules/api/geocoder/@tanstack/react-query.gen';
import { queryClient } from '@/setup/query-client';
import type { LocationFeature } from '@/types/LocationFeature';

export class Geocoder {
  public static SOURCES = [
    ...(Object.keys(Source) as Source[]),
    ...(Object.keys(CustomSource) as CustomSource[]).filter((source) => source !== CustomSource.csv),
  ];

  public static async reverse(point: Point): Promise<LocationFeature> {
    const coordinateFallback = `${point.coordinates[1].toFixed(6)}, ${point.coordinates[0].toFixed(6)}`;

    let feature: ReversePlaceFeature | undefined;
    try {
      const result = await queryClient.fetchQuery(
        geocoderApiV1ReverseOptions({
          query: {
            'point.lon': point.coordinates[0],
            'point.lat': point.coordinates[1],
            sources: Geocoder.SOURCES,
          },
        }),
      );
      feature = result.features?.[0];
    } catch {
      feature = undefined;
    }

    if (feature) {
      return {
        ...Geocoder.transformToLocationFeature(feature),
        geometry: point,
      };
    }

    return {
      type: 'Feature',
      geometry: point,
      properties: {
        name: {
          full: coordinateFallback,
          firstLine: coordinateFallback,
        },
      },
    };
  }

  public static transformToLocationFeature(feature: PlaceFeature): LocationFeature {
    const [firstLine, ...rest] = feature.properties.label.split(', ');

    return {
      type: 'Feature',
      geometry: feature.geometry as Point,
      properties: {
        peliasGid: feature.properties.gid,
        name: {
          full: feature.properties.label,
          firstLine,
          secondLine: rest.join(', '),
        },
      },
    };
  }

  public static areSame(locationFeatureA: LocationFeature, locationFeatureB: LocationFeature) {
    return (
      (!!locationFeatureA.properties.peliasGid &&
        locationFeatureA.properties.peliasGid === locationFeatureB.properties.peliasGid) ||
      (locationFeatureA.geometry.coordinates[0] === locationFeatureB.geometry.coordinates[0] &&
        locationFeatureA.geometry.coordinates[1] === locationFeatureB.geometry.coordinates[1])
    );
  }
}
